/**
 * UnifiedVisitNote Component
 * 
 * Displays a consolidated visit note with compliance checking and real-time prompts.
 * Part of the Unified Hospice Visit Synchronization & Compliance Agent.
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "sonner";
import axios from "axios";
import {
  FileText, CheckCircle, AlertTriangle, Clock, User, Calendar,
  ChevronDown, ChevronRight, Sparkles, Shield, Lock, Unlock,
  FileSignature, AlertCircle, Activity, Pill, Heart, Brain,
  Stethoscope, Users, ClipboardCheck, XCircle, Loader2, Check
} from "lucide-react";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

// Discipline display config
const DISCIPLINE_CONFIG = {
  rn: { label: "Registered Nurse", icon: Stethoscope, color: "text-blue-500", bg: "bg-blue-500/10" },
  lpn: { label: "Licensed Practical Nurse", icon: Stethoscope, color: "text-blue-400", bg: "bg-blue-400/10" },
  sw: { label: "Social Worker", icon: Users, color: "text-pink-500", bg: "bg-pink-500/10" },
  chaplain: { label: "Chaplain", icon: Heart, color: "text-purple-500", bg: "bg-purple-500/10" },
  cna: { label: "Certified Nursing Assistant", icon: Activity, color: "text-green-500", bg: "bg-green-500/10" },
  pt: { label: "Physical Therapy", icon: Activity, color: "text-orange-500", bg: "bg-orange-500/10" },
  ot: { label: "Occupational Therapy", icon: Activity, color: "text-amber-500", bg: "bg-amber-500/10" },
  slp: { label: "Speech Therapy", icon: Brain, color: "text-cyan-500", bg: "bg-cyan-500/10" },
  md: { label: "Physician", icon: Stethoscope, color: "text-violet-500", bg: "bg-violet-500/10" },
  np: { label: "Nurse Practitioner", icon: Stethoscope, color: "text-indigo-500", bg: "bg-indigo-500/10" },
  volunteer: { label: "Volunteer", icon: Heart, color: "text-teal-500", bg: "bg-teal-500/10" }
};

// Visit status config
const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-slate-500", icon: FileText },
  in_progress: { label: "In Progress", color: "bg-blue-500", icon: Clock },
  pending_compliance: { label: "Pending Compliance", color: "bg-amber-500", icon: AlertTriangle },
  ready_for_signature: { label: "Ready for Signature", color: "bg-emerald-500", icon: FileSignature },
  finalized: { label: "Finalized", color: "bg-green-500", icon: CheckCircle },
  locked: { label: "Locked", color: "bg-slate-600", icon: Lock }
};

const getAuthHeaders = () => {
  const token = localStorage.getItem("hospice_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function UnifiedVisitNote({ 
  visitId,
  patientId,
  patientName,
  isOpen,
  onClose,
  onVisitUpdated,
  isDark = true
}) {
  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [compliancePrompts, setCompliancePrompts] = useState([]);
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [signature, setSignature] = useState("");
  const [expandedSections, setExpandedSections] = useState(["smart_narrative_summary"]);
  const [finalizing, setFinalizing] = useState(false);
  const [aiRecommendations, setAiRecommendations] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(false);

  // Fetch visit data
  const fetchVisit = useCallback(async () => {
    if (!visitId) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/visit-sync/visit/${visitId}`,
        { headers: getAuthHeaders() }
      );
      setVisit(response.data);
      
      // Set compliance prompts from missing elements
      const missing = response.data.compliance_status?.missing_elements || [];
      setCompliancePrompts(missing.map(elem => ({
        element: elem.key,
        message: `${elem.label} not yet completed`,
        description: elem.description,
        isHardStop: response.data.compliance_status?.hard_stop_violations?.some(v => v.key === elem.key)
      })));
    } catch (err) {
      console.error("Failed to fetch visit:", err);
      toast.error("Failed to load visit details");
    } finally {
      setLoading(false);
    }
  }, [visitId]);

  useEffect(() => {
    if (isOpen && visitId) {
      fetchVisit();
    }
  }, [isOpen, visitId, fetchVisit]);

  // Run compliance check
  const runComplianceCheck = async () => {
    if (!visit) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/visit-sync/compliance-check`,
        { visit_id: visitId, discipline: visit.discipline },
        { headers: getAuthHeaders() }
      );
      
      setVisit(prev => ({
        ...prev,
        compliance_status: response.data.compliance
      }));
      setCompliancePrompts(response.data.prompts || []);
      
      if (response.data.compliance?.is_compliant) {
        toast.success("Visit is fully compliant!");
      } else {
        toast.warning(`${response.data.prompts?.length || 0} compliance items need attention`);
      }
    } catch (err) {
      console.error("Compliance check failed:", err);
      toast.error("Compliance check failed");
    } finally {
      setLoading(false);
    }
  };

  // Fetch AI-powered documentation recommendations
  const fetchAIRecommendations = async () => {
    if (!visit || !visit.compliance_status?.missing_elements?.length) {
      toast.info("No missing elements to get recommendations for");
      return;
    }
    
    setLoadingRecommendations(true);
    setShowRecommendations(true);
    
    try {
      const missingKeys = visit.compliance_status.missing_elements.map(e => e.key);
      const response = await axios.post(
        `${API_URL}/api/visit-sync/ai-recommendations`,
        {
          visit_id: visitId,
          missing_elements: missingKeys,
          patient_context: {
            patient_id: patientId,
            patient_name: patientName,
            discipline: visit.discipline
          }
        },
        { headers: getAuthHeaders() }
      );
      
      setAiRecommendations(response.data.recommendations || []);
      
      if (response.data.ai_enabled) {
        toast.success(`Generated ${response.data.total} AI recommendations`);
      } else {
        toast.info(`Generated ${response.data.total} template recommendations`);
      }
    } catch (err) {
      console.error("Failed to fetch AI recommendations:", err);
      toast.error("Failed to generate recommendations");
    } finally {
      setLoadingRecommendations(false);
    }
  };

  // Copy recommendation text to clipboard
  const copyRecommendation = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard - paste into documentation");
  };

  // Auto-fill a recommendation directly into the visit documentation
  const [autoFilling, setAutoFilling] = useState({});
  
  const autoFillRecommendation = async (recommendation) => {
    const elementKey = recommendation.element_key;
    setAutoFilling(prev => ({ ...prev, [elementKey]: true }));
    
    try {
      const response = await axios.post(
        `${API_URL}/api/visit-sync/auto-fill`,
        {
          visit_id: visitId,
          element_key: elementKey,
          content: recommendation.suggested_text
        },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success) {
        toast.success(`Auto-filled: ${recommendation.element_label}`);
        
        // Update local state with new compliance status
        if (response.data.new_compliance_status) {
          setVisit(prev => ({
            ...prev,
            compliance_status: response.data.new_compliance_status,
            consolidated_note: {
              ...prev?.consolidated_note,
              sections: {
                ...(prev?.consolidated_note?.sections || {}),
                [elementKey]: {
                  title: recommendation.element_label,
                  content: recommendation.suggested_text,
                  author: "Auto-filled",
                  auto_filled: true
                }
              }
            }
          }));
          
          // Update compliance prompts
          setCompliancePrompts(prev => prev.filter(p => p.element !== elementKey));
          
          // Remove from AI recommendations
          setAiRecommendations(prev => prev.filter(r => r.element_key !== elementKey));
        }
        
        onVisitUpdated?.();
      }
    } catch (err) {
      console.error("Auto-fill failed:", err);
      toast.error(err.response?.data?.detail || "Failed to auto-fill");
    } finally {
      setAutoFilling(prev => ({ ...prev, [elementKey]: false }));
    }
  };

  // Finalize visit
  const finalizeVisit = async () => {
    if (!signature.trim()) {
      toast.error("Please enter your electronic signature");
      return;
    }
    
    setFinalizing(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/visit-sync/visit/${visitId}/finalize`,
        {
          visit_id: visitId,
          signature: signature.trim(),
          attestation: "I certify that the information provided is accurate and complete."
        },
        { headers: getAuthHeaders() }
      );
      
      setVisit(prev => ({
        ...prev,
        status: "finalized",
        signature: signature.trim(),
        consolidated_note: response.data.consolidated_note
      }));
      
      toast.success("Visit finalized and locked successfully");
      setShowFinalizeDialog(false);
      onVisitUpdated?.();
    } catch (err) {
      console.error("Finalize failed:", err);
      const message = err.response?.data?.detail || "Failed to finalize visit";
      toast.error(message);
    } finally {
      setFinalizing(false);
    }
  };

  // Toggle section expansion
  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => 
      prev.includes(sectionKey)
        ? prev.filter(s => s !== sectionKey)
        : [...prev, sectionKey]
    );
  };

  // Get discipline config
  const getDisciplineConfig = (discipline) => {
    return DISCIPLINE_CONFIG[discipline?.toLowerCase()] || DISCIPLINE_CONFIG.rn;
  };

  // Get status config
  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  };

  if (!isOpen) return null;

  const disciplineConfig = visit ? getDisciplineConfig(visit.discipline) : DISCIPLINE_CONFIG.rn;
  const statusConfig = visit ? getStatusConfig(visit.status) : STATUS_CONFIG.draft;
  const DisciplineIcon = disciplineConfig.icon;
  const StatusIcon = statusConfig.icon;
  const compliancePercent = visit?.compliance_status?.completion_percentage || 0;
  const isCompliant = visit?.compliance_status?.is_compliant;
  const canFinalize = visit?.compliance_status?.can_finalize;
  const isFinalized = visit?.status === "finalized" || visit?.status === "locked";

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent 
        side="right" 
        className={`w-[900px] max-w-[95vw] overflow-y-auto ${
          isDark ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-gray-200 text-gray-900'
        }`}
      >
        <SheetHeader>
          <SheetTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <div className={`p-2 rounded-lg ${disciplineConfig.bg}`}>
              <DisciplineIcon className={`w-5 h-5 ${disciplineConfig.color}`} />
            </div>
            Unified Visit Note
            <Badge className={`ml-2 ${statusConfig.color} text-white`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
            </Badge>
          </SheetTitle>
          <SheetDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
            {patientName || "Patient"} - {visit?.date || "Today"}
          </SheetDescription>
        </SheetHeader>

        {loading && !visit ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
          </div>
        ) : visit ? (
          <div className="mt-6 space-y-6">
            {/* Visit Header */}
            <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-200'}>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Date</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar className="w-4 h-4 text-teal-500" />
                      <span className={isDark ? 'text-white' : 'text-gray-900'}>{visit.date}</span>
                    </div>
                  </div>
                  <div>
                    <Label className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Time In/Out</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock className="w-4 h-4 text-amber-500" />
                      <span className={isDark ? 'text-white' : 'text-gray-900'}>
                        {visit.time_in} - {visit.time_out}
                      </span>
                    </div>
                  </div>
                  <div>
                    <Label className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Duration</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Activity className="w-4 h-4 text-blue-500" />
                      <span className={isDark ? 'text-white' : 'text-gray-900'}>
                        {visit.duration_minutes} minutes
                      </span>
                    </div>
                  </div>
                  <div>
                    <Label className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Clinician</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <User className="w-4 h-4 text-violet-500" />
                      <span className={isDark ? 'text-white' : 'text-gray-900'}>{visit.clinician_name}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Compliance Status Card */}
            <Card className={`border-2 ${
              isCompliant 
                ? isDark ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200'
                : canFinalize
                  ? isDark ? 'bg-amber-500/10 border-amber-500/30' : 'bg-amber-50 border-amber-200'
                  : isDark ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'
            }`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className={`text-base flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    <Shield className={`w-5 h-5 ${
                      isCompliant ? 'text-emerald-500' : canFinalize ? 'text-amber-500' : 'text-red-500'
                    }`} />
                    CMS Compliance Check
                    {isCompliant && (
                      <Badge className="bg-emerald-500 text-white">
                        <Check className="w-3 h-3 mr-1" />
                        Compliant
                      </Badge>
                    )}
                  </CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={runComplianceCheck}
                    disabled={loading || isFinalized}
                    className={isDark ? 'border-slate-600' : ''}
                  >
                    <ClipboardCheck className="w-4 h-4 mr-1" />
                    Re-check
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-4">
                  {/* Progress bar */}
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className={isDark ? 'text-slate-400' : 'text-gray-600'}>Completion</span>
                      <span className={`font-medium ${
                        compliancePercent >= 100 ? 'text-emerald-500' :
                        compliancePercent >= 70 ? 'text-amber-500' : 'text-red-500'
                      }`}>{compliancePercent}%</span>
                    </div>
                    <Progress 
                      value={compliancePercent} 
                      className={`h-2 ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}
                    />
                  </div>

                  {/* Compliance Prompts - Smart Prompts for Missing Elements */}
                  {compliancePrompts.length > 0 && !isFinalized && (
                    <div className="space-y-2">
                      <Label className={`text-sm font-medium flex items-center gap-2 ${
                        isDark ? 'text-slate-300' : 'text-gray-700'
                      }`}>
                        <Sparkles className="w-4 h-4 text-violet-400" />
                        Smart Prompts - Complete These Items:
                      </Label>
                      <div className="space-y-2">
                        {compliancePrompts.map((prompt, idx) => (
                          <div 
                            key={idx}
                            className={`flex items-start gap-3 p-3 rounded-lg ${
                              prompt.isHardStop
                                ? isDark ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200'
                                : isDark ? 'bg-slate-800/50' : 'bg-white border border-gray-200'
                            }`}
                          >
                            {prompt.isHardStop ? (
                              <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                            ) : (
                              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {prompt.message}
                                {prompt.isHardStop && (
                                  <Badge variant="destructive" className="ml-2 text-xs">
                                    Required
                                  </Badge>
                                )}
                              </p>
                              <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                {prompt.description}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Completed Elements */}
                  {visit.compliance_status?.completed_elements?.length > 0 && (
                    <div>
                      <Label className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                        Completed ({visit.compliance_status.completed_elements.length}):
                      </Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {visit.compliance_status.completed_elements.map((elem, idx) => (
                          <Badge 
                            key={idx}
                            variant="outline"
                            className={`text-xs ${
                              isDark 
                                ? 'border-emerald-500/50 text-emerald-400 bg-emerald-500/10' 
                                : 'border-emerald-500 text-emerald-600 bg-emerald-50'
                            }`}
                          >
                            <Check className="w-3 h-3 mr-1" />
                            {elem.label}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI Documentation Recommendations Button */}
                  {compliancePrompts.length > 0 && !isFinalized && (
                    <div className="pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchAIRecommendations}
                        disabled={loadingRecommendations}
                        className={`w-full ${isDark ? 'border-violet-500/50 hover:bg-violet-500/10' : 'border-violet-500 hover:bg-violet-50'}`}
                        data-testid="ai-recommendations-btn"
                      >
                        {loadingRecommendations ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Sparkles className="w-4 h-4 mr-2 text-violet-500" />
                        )}
                        Get AI Documentation Suggestions
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* AI Recommendations Panel */}
            {showRecommendations && (
              <Card className={`border-2 ${isDark ? 'bg-violet-500/5 border-violet-500/30' : 'bg-violet-50 border-violet-200'}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className={`text-base flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      <Sparkles className="w-5 h-5 text-violet-500" />
                      AI Documentation Suggestions
                      <Badge className="bg-violet-500 text-white text-xs">
                        {aiRecommendations.length} suggestions
                      </Badge>
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowRecommendations(false)}
                      className={isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                  <CardDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                    Click any suggestion to copy it, then paste into your documentation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingRecommendations ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-violet-500 mr-2" />
                      <span className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                        Generating AI suggestions...
                      </span>
                    </div>
                  ) : aiRecommendations.length > 0 ? (
                    <div className="space-y-3">
                      {aiRecommendations.map((rec, idx) => (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg transition-colors ${
                            isDark 
                              ? 'bg-slate-800/50 border border-slate-700' 
                              : 'bg-white border border-gray-200'
                          }`}
                          data-testid={`ai-rec-${rec.element_key}`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={`text-xs ${
                                isDark ? 'border-violet-500/50 text-violet-400' : 'border-violet-500 text-violet-600'
                              }`}>
                                {rec.element_label}
                              </Badge>
                              {rec.is_ai_generated && (
                                <Badge className="bg-violet-500/20 text-violet-400 text-xs border-0">
                                  <Sparkles className="w-3 h-3 mr-1" />
                                  AI Generated
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyRecommendation(rec.suggested_text)}
                                className={`h-7 px-2 ${isDark ? 'text-slate-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
                                title="Copy to clipboard"
                              >
                                <FileText className="w-3 h-3 mr-1" />
                                Copy
                              </Button>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => autoFillRecommendation(rec)}
                                disabled={autoFilling[rec.element_key]}
                                className="h-7 px-3 bg-emerald-500 hover:bg-emerald-600 text-white"
                                data-testid={`auto-fill-${rec.element_key}`}
                              >
                                {autoFilling[rec.element_key] ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <Check className="w-3 h-3 mr-1" />
                                )}
                                Auto-Fill
                              </Button>
                            </div>
                          </div>
                          <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                            {rec.suggested_text}
                          </p>
                        </div>
                      ))}
                      
                      {/* Bulk Auto-Fill Option */}
                      {aiRecommendations.length > 1 && (
                        <div className="pt-2 border-t border-slate-700/50">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              for (const rec of aiRecommendations) {
                                await autoFillRecommendation(rec);
                              }
                            }}
                            disabled={Object.values(autoFilling).some(v => v)}
                            className={`w-full ${isDark ? 'border-emerald-500/50 hover:bg-emerald-500/10' : 'border-emerald-500 hover:bg-emerald-50'}`}
                            data-testid="auto-fill-all-btn"
                          >
                            <Sparkles className="w-4 h-4 mr-2 text-emerald-500" />
                            Auto-Fill All ({aiRecommendations.length} items)
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={`text-center py-8 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                      <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No recommendations available</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Consolidated Note Sections */}
            <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-base flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <FileText className="w-5 h-5 text-blue-400" />
                  Consolidated Visit Note
                </CardTitle>
                <CardDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                  {visit.documentation_components?.length || 0} documentation fragments merged
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="max-h-[400px]">
                  <div className="p-4 space-y-2">
                    {visit.consolidated_note?.sections && Object.entries(visit.consolidated_note.sections).length > 0 ? (
                      Object.entries(visit.consolidated_note.sections).map(([key, section]) => (
                        <Collapsible
                          key={key}
                          open={expandedSections.includes(key)}
                          onOpenChange={() => toggleSection(key)}
                        >
                          <CollapsibleTrigger className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                            isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-100'
                          }`}>
                            <div className="flex items-center gap-2">
                              {expandedSections.includes(key) ? (
                                <ChevronDown className="w-4 h-4 text-slate-400" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-slate-400" />
                              )}
                              <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {section.title}
                              </span>
                            </div>
                            <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                              {section.author}
                            </span>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className={`ml-6 p-3 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-gray-50'}`}>
                              {typeof section.content === 'string' ? (
                                <p className={`text-sm whitespace-pre-wrap ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                                  {section.content}
                                </p>
                              ) : (
                                <div className="space-y-2">
                                  {Object.entries(section.content || {}).map(([k, v]) => (
                                    <div key={k} className="flex gap-2">
                                      <span className={`text-sm font-medium capitalize ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                        {k.replace(/_/g, ' ')}:
                                      </span>
                                      <span className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                                        {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      ))
                    ) : (
                      <div className={`text-center py-8 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                        <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No documentation sections yet</p>
                        <p className="text-xs mt-1">Add documentation to build the unified note</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Source Documents */}
            {visit.consolidated_note?.source_documents?.length > 0 && (
              <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}>
                <CardHeader className="pb-2">
                  <CardTitle className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Source Documents ({visit.consolidated_note.source_documents.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="flex flex-wrap gap-2">
                    {visit.consolidated_note.source_documents.map((doc, idx) => (
                      <Badge 
                        key={idx}
                        variant="outline"
                        className={isDark ? 'border-slate-600 text-slate-300' : 'border-gray-300 text-gray-600'}
                      >
                        {doc.type.replace(/_/g, ' ')}
                        <span className="ml-1 text-xs opacity-70">• {doc.author}</span>
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Signature Section (if finalized) */}
            {isFinalized && visit.signature && (
              <Card className={isDark ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200'}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Lock className="w-5 h-5 text-emerald-500" />
                      <div>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          Electronically Signed
                        </p>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          {visit.signature} - {visit.finalized_at ? new Date(visit.finalized_at).toLocaleString() : ""}
                        </p>
                      </div>
                    </div>
                    <Badge className="bg-emerald-500 text-white">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Finalized
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Action Buttons */}
            <div className={`flex justify-end gap-3 pt-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
              <Button variant="outline" onClick={onClose} className={isDark ? 'border-slate-600' : ''}>
                Close
              </Button>
              {!isFinalized && (
                <Button
                  onClick={() => setShowFinalizeDialog(true)}
                  disabled={!canFinalize || loading}
                  className={canFinalize ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                >
                  <FileSignature className="w-4 h-4 mr-2" />
                  {canFinalize ? 'Sign & Finalize' : 'Complete Required Items First'}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className={`text-center py-12 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
            <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No visit data available</p>
          </div>
        )}

        {/* Finalize Dialog */}
        <Dialog open={showFinalizeDialog} onOpenChange={setShowFinalizeDialog}>
          <DialogContent className={isDark ? 'bg-slate-900 border-slate-700' : 'bg-white'}>
            <DialogHeader>
              <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>
                Finalize Visit Note
              </DialogTitle>
              <DialogDescription className={isDark ? 'text-slate-400' : 'text-gray-500'}>
                By signing, you certify that all information is accurate and complete.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className={`p-3 rounded-lg ${isDark ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-amber-50 border border-amber-200'}`}>
                <p className={`text-sm ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                  <AlertTriangle className="w-4 h-4 inline mr-2" />
                  Once finalized, this visit note will be locked and cannot be modified.
                </p>
              </div>

              <div className="space-y-2">
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                  Electronic Signature <span className="text-red-500">*</span>
                </Label>
                <Input
                  placeholder="Type your full name to sign"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  className={isDark ? 'bg-slate-800 border-slate-700 text-white' : ''}
                  data-testid="finalize-signature-input"
                />
              </div>

              <div className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                <p className="font-medium mb-1">Attestation:</p>
                <p>I certify that the information provided is accurate and complete. I have reviewed all documentation components and confirm they meet CMS hospice visit requirements.</p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowFinalizeDialog(false)} className={isDark ? 'border-slate-600' : ''}>
                Cancel
              </Button>
              <Button
                onClick={finalizeVisit}
                disabled={!signature.trim() || finalizing}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="finalize-submit-btn"
              >
                {finalizing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Finalizing...
                  </>
                ) : (
                  <>
                    <FileSignature className="w-4 h-4 mr-2" />
                    Sign & Finalize
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Unified Visit Timeline Tile Component
 * Displays a single unified visit on the patient timeline
 */
export function UnifiedVisitTile({ 
  visit, 
  onClick, 
  isDark = true 
}) {
  const statusConfig = STATUS_CONFIG[visit.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;
  const disciplineConfig = DISCIPLINE_CONFIG[visit.discipline?.toLowerCase()] || DISCIPLINE_CONFIG.rn;
  const DisciplineIcon = disciplineConfig.icon;
  const compliancePercent = visit.compliance_status?.completion_percentage || 0;
  const isCompliant = visit.compliance_status?.is_compliant;

  return (
    <div 
      className={`relative pl-14 group cursor-pointer`}
      onClick={() => onClick?.(visit)}
      data-testid={`unified-visit-tile-${visit.visit_id}`}
    >
      {/* Visit Marker */}
      <div className={`absolute left-4 w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center ring-4 ${
        isDark ? 'ring-slate-950' : 'ring-gray-50'
      }`}>
        <DisciplineIcon className="w-3 h-3 text-white" />
      </div>
      
      {/* Visit Card */}
      <Card className={`transition-all ${
        isDark 
          ? 'bg-slate-800/50 border-slate-700 hover:bg-slate-800 hover:border-teal-500/50' 
          : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-teal-500 shadow-sm'
      }`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Badge className={`${statusConfig.color} text-white text-xs`}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {statusConfig.label}
                </Badge>
                <Badge variant="outline" className={`text-xs ${isDark ? 'border-slate-600' : 'border-gray-300'}`}>
                  {disciplineConfig.label}
                </Badge>
                {isCompliant && (
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-0 text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    CMS Compliant
                  </Badge>
                )}
              </div>
              
              <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Unified Visit Note
              </h4>
              
              <div className={`flex items-center gap-4 mt-2 text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {visit.date}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {visit.time_in} - {visit.time_out}
                </span>
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {visit.clinician_name}
                </span>
              </div>
              
              {/* Compliance Progress */}
              <div className="mt-3 flex items-center gap-3">
                <Progress 
                  value={compliancePercent} 
                  className={`h-1.5 flex-1 ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}
                />
                <span className={`text-xs font-medium ${
                  compliancePercent >= 100 ? 'text-emerald-500' :
                  compliancePercent >= 70 ? 'text-amber-500' : 'text-red-500'
                }`}>
                  {compliancePercent}%
                </span>
              </div>
            </div>
            
            <ChevronRight className={`w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity ${
              isDark ? 'text-slate-400' : 'text-gray-400'
            }`} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
